import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const UniversityContext = createContext({});

export const UniversityProvider = ({ children }) => {
  const { user, profile, isLoading: authLoading } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [activeUniversity, setActiveUniversity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (user && profile) {
      loadUserUniversities();
    } else {
      setUniversities([]);
      setActiveUniversity(null);
      setIsLoading(false);
    }
  }, [user, profile, authLoading]);

  const loadUserUniversities = async () => {
    try {
      setIsLoading(true);

      if (profile.role === 'super_admin') {
        const { data, error } = await supabase
          .from('universities')
          .select('*')
          .order('name');

        if (error) throw error;
        setUniversities(data || []);
        setActiveUniversity(null);
      } else {
        const { data: rolesData, error: rolesError } = await supabase
          .from('user_university_roles')
          .select(`
            id,
            university_id,
            role,
            is_active,
            university:universities(*)
          `)
          .eq('user_id', user.id);

        if (rolesError) throw rolesError;

        const universityList = rolesData.map(r => ({
          ...r.university,
          userRole: r.role,
          isActive: r.is_active,
          roleId: r.id
        }));

        setUniversities(universityList);

        const active = universityList.find(u => u.isActive);
        if (active) {
          setActiveUniversity(active);
        } else if (universityList.length > 0) {
          await switchUniversity(universityList[0].id);
        } else if (profile.university_id) {
          const { data: universityData, error: uniError } = await supabase
            .from('universities')
            .select('*')
            .eq('id', profile.university_id)
            .maybeSingle();

          if (!uniError && universityData) {
            setActiveUniversity(universityData);
          }
        }
      }
    } catch (error) {
      console.error('Error loading universities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchUniversity = async (universityId) => {
    try {
      if (profile.role === 'super_admin') {
        if (universityId === null) {
          setActiveUniversity(null);
        } else {
          const university = universities.find(u => u.id === universityId);
          if (university) {
            setActiveUniversity(university);
          }
        }
        return;
      }

      await supabase
        .from('user_university_roles')
        .update({ is_active: false })
        .eq('user_id', user.id);

      const { error } = await supabase
        .from('user_university_roles')
        .update({ is_active: true })
        .eq('user_id', user.id)
        .eq('university_id', universityId);

      if (error) throw error;

      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ university_id: universityId })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      await loadUserUniversities();
    } catch (error) {
      console.error('Error switching university:', error);
      throw error;
    }
  };

  const value = {
    universities,
    activeUniversity,
    isLoading,
    switchUniversity,
    hasMultipleUniversities: universities.length > 1,
    isSuperAdmin: profile?.role === 'super_admin'
  };

  return (
    <UniversityContext.Provider value={value}>
      {children}
    </UniversityContext.Provider>
  );
};

export const useUniversity = () => {
  const context = useContext(UniversityContext);
  if (!context) {
    throw new Error('useUniversity must be used within UniversityProvider');
  }
  return context;
};
